import { Box, Flex, Menu, MenuButton, Text, useBoolean } from '@chakra-ui/react';
import { Suspense, lazy, useEffect, useState } from 'react';
import { FiMoreHorizontal, FiRefreshCw, FiZap } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';

import { ActionMenuList } from 'components/ActionMenuList';
import DuplicateIconV1 from 'components/Icons/DuplicateIconV1';
import FigmaEditIcon from 'components/Icons/FigmaEditIcon';
import FigmaSettingsSlidersIcon from 'components/Icons/FigmaSettingsSlidersIcon';
import FigmaPlusIcon from 'components/Icons/FigmaPlusIcon';
import FigmaTrashIcon from 'components/Icons/FigmaTrashIcon';
import DeleteModal from 'components/Modals/DeleteModal';
import { PLAN_TEXT } from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';
import {
  clearStepperFormData,
  deleteActionData,
  duplicateActionFile,
  editActionDetails,
  getActionData,
  getActionList,
  getOneActionData,
  getSelectedAction,
  setActionCreateForm,
  setActionDetails,
  setActionFormEdit,
  setClearSelectedAreasTable,
  versionData,
} from 'redux/DataImportSlice';
import { fontSize } from 'styles/revampTokens';

import { resolveActionColor } from './actionColors';
import colors from './dataTheme';
import {
  CountPill,
  SectionTitle,
  cardStyle,
  iconButtonStyle,
  primaryButtonStyle,
} from './detailUi';
import EditSourceModal from './EditSourceModal';

const LazyMappingDimensionModal = lazy(() => import('./MappingDimensionModal'));
const LazyMappingDataModal = lazy(() => import('./MappingDataModel'));

interface Properties {
  dataSourceId: number;
  onCreateAction: () => void;
}

const ActionsBar = ({ dataSourceId, onCreateAction }: Properties) => {
  const dispatch: any = useDispatch();
  const actions: any[] = useSelector(getActionList) ?? [];
  const selectedAction = useSelector(getSelectedAction);

  const [dimensionModal, setDimensionModal] = useBoolean();
  const [dataModal, setDataModal] = useBoolean();
  const [runTarget, setRunTarget] = useState<any>(null);
  const [renameTarget, setRenameTarget] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  // Mounting (first open, or coming back from the create form) refreshes both lists.
  useEffect(() => {
    if (dataSourceId) {
      dispatch(versionData(dataSourceId));
      dispatch(getActionData(Number(dataSourceId)));
    }
  }, [dataSourceId]);

  const refetch = () => dispatch(getActionData(dataSourceId));

  const selectAction = (action: any) => {
    dispatch(setActionDetails({ actionId: action.id }));
    dispatch(getOneActionData({ dataSourceId, actionId: action.id }));
  };

  const runAction = (action: any) => {
    setRunTarget(action);
    if (action.action_type === 'dimension') setDimensionModal.on();
    else setDataModal.on();
  };

  const editAction = (action: any) => {
    dispatch(getOneActionData({ dataSourceId, actionId: action.id }));
    onCreateAction();
    dispatch(setActionCreateForm(true));
    dispatch(
      setActionFormEdit({
        isEditForm: true,
        actionId: action.id,
        actionType: action.action_type,
        scenarioId: action.scenario_id,
      }),
    );
  };

  const addAction = async () => {
    await dispatch(clearStepperFormData());
    await dispatch(setClearSelectedAreasTable());
    onCreateAction();
    await dispatch(setActionCreateForm(true));
  };

  const saveName = async (name: string) => {
    if (!renameTarget) return;
    await dispatch(
      editActionDetails({
        dataSourceId,
        actionId: renameTarget.id,
        request_body: { name },
        toaster: {
          successMessage: 'Successfully updated',
          errorMessage: 'An action with this name already exists',
        },
      }),
    );
    setRenameTarget(null);
    refetch();
  };

  const duplicate = async (action: any) => {
    await dispatch(duplicateActionFile({ dataSourceId, actionId: action.id }));
    refetch();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await dispatch(deleteActionData({ dataSourceId, actionId: deleteTarget.id }));
    setDeleteTarget(null);
    refetch();
  };

  const renderChip = (action: any) => {
    const isSelected = selectedAction?.actionId === action.id;
    const bubbleColor = resolveActionColor(action);
    return (
      <Flex
        key={action.id}
        role='group'
        alignItems='center'
        gap={2}
        h='32px'
        pl='10px'
        pr='4px'
        flexShrink={0}
        cursor='pointer'
        border='1px solid'
        borderColor={isSelected ? colors.primary : colors.border}
        borderRadius='8px'
        bg={isSelected ? colors.activeBg : colors.surface}
        transition='background 0.12s ease, border-color 0.12s ease'
        _hover={{ bg: isSelected ? colors.activeBg : colors.hover }}
        onClick={() => selectAction(action)}
      >
        <Box w='8px' h='8px' borderRadius='full' flexShrink={0} bg={bubbleColor} />
        <Text
          fontSize={fontSize.body}
          fontWeight={isSelected ? 600 : 500}
          color={isSelected ? colors.primary : colors.textDark}
          maxW='200px'
          noOfLines={1}
          title={action.name}
        >
          {action.name}
        </Text>
        <Flex
          {...iconButtonStyle}
          role='button'
          aria-label={`Run ${action.name}`}
          title='Run'
          color={colors.primary}
          w='0'
          minW='0'
          ml='-8px'
          opacity={0}
          overflow='hidden'
          pointerEvents='none'
          transition='width 0.2s ease, margin 0.2s ease, opacity 0.18s ease, background 0.12s ease'
          _groupHover={{ w: '24px', minW: '24px', ml: 0, opacity: 1, pointerEvents: 'auto' }}
          _hover={{ bg: colors.activeBg, color: colors.primary }}
          sx={{
            '& svg': { transition: 'transform 0.35s ease', flexShrink: 0 },
            '&:hover svg': { transform: 'rotate(180deg)' },
          }}
          onClick={(event) => {
            event.stopPropagation();
            runAction(action);
          }}
        >
          <FiRefreshCw size={13} />
        </Flex>
        <Menu placement='bottom-end' strategy='fixed'>
          <MenuButton
            {...iconButtonStyle}
            aria-label={`Actions for ${action.name}`}
            onClick={(event) => event.stopPropagation()}
          >
            <FiMoreHorizontal size={15} />
          </MenuButton>
          <ActionMenuList
            items={[
              {
                key: 'rename',
                label: 'Rename',
                icon: <FigmaEditIcon width={14} height={14} color={colors.textDark} />,
                onSelect: () => setRenameTarget(action),
              },
              {
                key: 'edit',
                label: 'Edit',
                icon: <FigmaSettingsSlidersIcon width={14} height={14} color={colors.textDark} />,
                onSelect: () => editAction(action),
              },
              {
                key: 'duplicate',
                label: 'Duplicate',
                icon: <DuplicateIconV1 size={14} />,
                onSelect: () => duplicate(action),
              },
              {
                key: 'delete',
                label: 'Delete',
                icon: <FigmaTrashIcon width={14} height={14} color={colors.danger} />,
                color: colors.danger,
                onSelect: () => setDeleteTarget(action),
              },
            ]}
          />
        </Menu>
      </Flex>
    );
  };

  return (
    <Flex {...cardStyle} px={4} py={3} flexShrink={0} alignItems='center' gap={3}>
      <Box flex={1} minW={0}>
        <Flex alignItems='center' gap={2} mb={2}>
          <SectionTitle>Actions</SectionTitle>
          <CountPill>{actions.length}</CountPill>
        </Flex>

        <Flex gap={2} overflowX='auto' className='scroll' sx={{ scrollbarWidth: 'thin' }}>
          {actions.length > 0 ? (
            actions.map((action) => renderChip(action))
          ) : (
            <Flex alignItems='center' gap={2} h='32px'>
              <Flex
                w='24px'
                h='24px'
                alignItems='center'
                justifyContent='center'
                borderRadius='full'
                bg={colors.activeBg}
                color={colors.primary}
              >
                <FiZap size={13} />
              </Flex>
              <Text {...PLAN_TEXT.body} color={colors.textSecondary}>
                No actions yet — add one to import data from this source.
              </Text>
            </Flex>
          )}
        </Flex>
      </Box>

      <Flex as='button' type='button' {...primaryButtonStyle} onClick={addAction}>
        <FigmaPlusIcon width={12} height={12} color={colors.white} />
        Add Action
      </Flex>

      <EditSourceModal
        isOpen={Boolean(renameTarget)}
        initialName={renameTarget?.name ?? ''}
        title='Edit action'
        fieldLabel='Action name'
        onClose={() => setRenameTarget(null)}
        onSave={saveName}
      />
      <DeleteModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        itemName={deleteTarget?.name ?? ''}
        onDelete={confirmDelete}
      />
      <Suspense fallback={null}>
        {dimensionModal && (
          <LazyMappingDimensionModal
            mappingModal={dimensionModal}
            setMappingModal={setDimensionModal}
            selectedActionId={runTarget?.id}
            dataSourceId={dataSourceId}
            selectedMapType={runTarget?.action_type}
            scenarioId={runTarget?.scenario_id}
          />
        )}
        {dataModal && (
          <LazyMappingDataModal
            mappingModal={dataModal}
            setMappingModal={setDataModal}
            selectedActionId={runTarget?.id}
            dataSourceId={dataSourceId}
            selectedMapType={runTarget?.action_type}
            scenarioId={runTarget?.scenario_id}
          />
        )}
      </Suspense>
    </Flex>
  );
};

export default ActionsBar;
